import {createSearchParams, useParams} from 'react-router-dom'
import { NO_DATA_AVAILABLE } from '../../../constants/common'
import {deleteRequest, getRequest} from '../../../services/base'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {ConnectorContext, SELECT_CONNECTOR, SET_CONNECTOR_LIST,} from './context/context'
import InputText from '../../common/InputText'
import ConnectorTable from './ConnectorTable'
import ConnectorFormModal from './form'
import CloneSvgModal from './CloneSvgModal'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {buildPageUrl, downloadFileBlob, getDeleteTargetPage} from '../../../utils/common'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Button, Flex, Form, Modal, Row, Select, Space} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const SVG_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'with_svg', label: 'With Connector Layout' },
  { value: 'without_svg', label: 'Without Connector Layout' },
]

const LOCATION_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'With Vehicle Views' },
  { value: 'no', label: 'Without Vehicle Views' },
]

const ConnectorList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(ConnectorContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [categoryOptions, setCategoryOptions] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedSvgFilter, setSelectedSvgFilter] = useState('all')
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('all')
  const [pagination, setPagination] = useState({})
  const [cloneSvgModalOpen, setCloneSvgModalOpen] = useState(false)
  const currentUrl = useRef(`/api/connector/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    getConnectorListData()
    getConnectorCategoryList()

    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.connector' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
  }, [])

  useEffect(() => {
    currentUrl.current = buildUrl({
      vehicle,
      search,
      category: selectedCategory?.value || '',
      svg_filter: selectedSvgFilter,
      location_filter: selectedLocationFilter,
    })
    getConnectorListData()
  }, [debouncedSearch, selectedCategory, selectedSvgFilter, selectedLocationFilter])

  const buildUrl = ({ vehicle, search, category, svg_filter, location_filter, limit, offset }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      category,
      svg_filter,
      location_filter,
    })
    if (limit) params.set('limit', limit)
    if (offset) params.set('offset', offset)
    return `/api/connector/?${params.toString()}`
  }

  const getConnectorListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'connectors.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_CONNECTOR_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'connectors.loadError' }))
    }
  }

  const getConnectorCategoryList = async () => {
    let res = await getRequest(`/api/connector/category/?vehicle=${vehicle}`)
    setCategoryOptions(
      (res.data || []).map((val) => ({
        value: val,
        label: val.charAt(0).toUpperCase() + val.slice(1),
      }))
    )
  }

  const onEdit = (record) => {
    dispatch({
      type: SELECT_CONNECTOR,
      data: { selected_connector: record, show_connector_popup: true },
    })
  }

  const deleteConnector = async (record) => {
    let res = await deleteRequest(
      `/api/connector/delete/${vehicle}/${record.name}/`
    )
    if (res?.status) {
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getConnectorListData()
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'connectors.deleteSuccess' }),
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'connectors.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'connectors.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'connectors.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => {
        deleteConnector(record)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const exportConnector = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'connectors.downloading' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(
      `/api/connector/download/?vehicle=${vehicle}&search=${search}&category=${selectedCategory?.value || ''}&svg_filter=${selectedSvgFilter}&location_filter=${selectedLocationFilter}`,
      true,
      true
    )

    // `res?.msg` is redundant: base.js sets status false on every failure,
    // and an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type:
            res?.code === NO_DATA_AVAILABLE ? 'info' : 'error',
          msg: intl.formatMessage({
            id:
              res?.code === NO_DATA_AVAILABLE
                ? 'generic.noDataToExport'
                : 'connectors.exportError',
          }),
        },
      })
    } else {
      downloadFileBlob(res, `${vehicle}_connectors.csv`)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'connectors.exportSuccess' }),
        },
      })
    }
  }

  const downloadSvgFile = async (record) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'connectors.downloadingSvg' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(
      `/api/connector/download/svg/${record.name}/?vehicle=${vehicle}`,
      true,
      true
    )
    // Was `if (res?.msg)`: a failure carrying no message fell through and saved
    // the error object as the .svg.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'connectors.downloadSvgError' }),
        },
      })
    } else {
      downloadFileBlob(res, `${record.name}.zip`)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'connectors.downloadSvgSuccess' }),
        },
      })
    }
  }

  const onCloneSvg = (record) => {
    dispatch({
      type: SELECT_CONNECTOR,
      data: { selected_connector: record, show_connector_popup: false },
    })
    setCloneSvgModalOpen(true)
  }

  const showLocation = (record) => {
    if (!record.location?.length) return
    modal.info({
      title: intl.formatMessage({ id: 'connectors.hotSpot' }),
      content: (
        <ul style={{ maxHeight: 200, overflow: 'auto', paddingLeft: 20 }}>
          {record.location.map((loc, idx) => (
            <li key={`loc-${idx}`}>{loc}</li>
          ))}
        </ul>
      ),
      centered: true,
    })
  }

  // Build data source for table
  const dataSource = useMemo(() => {
    if (!state?.connector_list?.length) return []

    return state?.connector_list?.map((item, index) => ({
      ...item,
      key: `connector-${index}`,
      rowKey: `connector-${item.id}`,
      index: index,
      device_display: item.device_data?.name || '-',
      has_svg:
        item.svg !== null && item.svg !== undefined && item.svg !== ''
          ? 'yes'
          : 'no',
      has_location: item.location?.length > 0 ? 'yes' : 'no',
    }))
  }, [state.connector_list])

  return (
    <>
      {contextHolder}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" className="full-width" gap={16}>
              <Flex gap={16} className='filters-wrapper full-width'>
                <InputText
                  name="search_connector"
                  type="search"
                  placeholder={intl.formatMessage({
                    id: 'connectors.searchPlaceholder',
                  })}
                  value={search}
                  isRequired={false}
                  wrapperClass="search-connector"
                  onChange={(value) => {
                    setSearch(value)
                  }}
                />

                <Select
                  placeholder={intl.formatMessage({ id: 'connectors.selectCategory' })}
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={(value) => setSelectedCategory(value)}
                  className="select-category"
                  allowClear
                  labelInValue
                />

                <Select
                  placeholder={intl.formatMessage({ id: 'connectors.selectSvgFilter' })}
                  options={SVG_FILTER_OPTIONS}
                  value={selectedSvgFilter}
                  onChange={(value) => setSelectedSvgFilter(value)}
                  allowClear={false}
                  className="select-svg"
                />

                <Select
                  placeholder={intl.formatMessage({ id: 'connectors.selectLocationFilter' })}
                  options={LOCATION_FILTER_OPTIONS}
                  value={selectedLocationFilter}
                  onChange={(value) => setSelectedLocationFilter(value)}
                  allowClear={false}
                  className="select-location"
                />
              </Flex>

              <Space size="middle">
                <Button onClick={exportConnector}>
                  {intl.formatMessage({ id: 'connectors.export' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <ConnectorFormModal getConnectorListData={getConnectorListData} />

          <CloneSvgModal
            open={cloneSvgModalOpen}
            onClose={() => setCloneSvgModalOpen(false)}
            onSuccess={() => {
              setCloneSvgModalOpen(false)
              getConnectorListData()
            }}
          />

          <Flex style={{ width: '100%' }}>
            <ConnectorTable
              dataSource={dataSource}
              pagination={pagination}
              getConnectorListData={getConnectorListData}
              onEdit={onEdit}
              onDelete={confirmDelete}
              onCloneSvg={onCloneSvg}
              onDownloadSvg={downloadSvgFile}
              onShowLocation={showLocation}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default ConnectorList
